// =============================================================================
// teams.js — who is in which team (Oct 2026)
// =============================================================================
// `schedule.teams[key] = {label, members: [staff ids]}` (src/logic/teams.py).
// A person is in at most one team. A team does nothing by itself: the
// department's rules say what teams mean. The key never changes, so a rename
// is a label edit; nothing about a team is stored on the person.
// =============================================================================
import { idOf } from './people';

export const NO_TEAM_LABEL = 'Unassigned';

/** The department's teams, as [{key, label, members}], ordered by label. */
export function teamsOf(schedule) {
  const teams = schedule?.teams;
  if (!teams || Array.isArray(teams)) return [];
  return Object.entries(teams)
    .map(([key, t]) => ({ key, label: t?.label || key, members: t?.members || [] }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
}

/** The team (as {key, label, members}) a person is in, or null. */
export function teamOf(teams, person) {
  const id = idOf(person);
  return (id && teams.find(t => t.members.includes(id))) || null;
}

/** `teams` (the stored object) after putting `staffId` in team `teamKey`
 * ('' / null = no team), mirroring DataService.set_team: out of any other
 * team. Returns a new object. */
export function withTeam(teams, staffId, teamKey) {
  const out = {};
  Object.entries(teams || {}).forEach(([key, t]) => {
    const members = (t.members || []).filter(m => m !== staffId);
    if (key === teamKey) members.push(staffId);
    out[key] = { ...t, members };
  });
  return out;
}
